import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";

const HTTP_UNAUTHORIZED = 401;
const AuthContext = createContext(null);

// Endpoints differ by role
const ME_ENDPOINTS = {
  admin: "/auth/me",
  customer: "/customer/me",
};

export function AuthProvider({ children }) {
  // null = checking; false = unauthenticated; object = authenticated
  const [user, setUser] = useState(null);

  // Try customer first (more common), then admin. Single shared cookie.
  useEffect(() => {
    let mounted = true;
    const tryFetch = async () => {
      try {
        const { data } = await api.get(ME_ENDPOINTS.customer);
        if (mounted) setUser(data);
        return;
      } catch (err) {
        if (err?.response?.status !== HTTP_UNAUTHORIZED) {
          console.error("[auth] customer/me failed:", err);
        }
      }
      try {
        const { data } = await api.get(ME_ENDPOINTS.admin);
        if (mounted) setUser(data);
        return;
      } catch (err) {
        if (err?.response?.status !== HTTP_UNAUTHORIZED) {
          console.error("[auth] auth/me failed:", err);
        }
      }
      if (mounted) setUser(false);
    };
    tryFetch();
    return () => {
      mounted = false;
    };
  }, []);

  const adminLogin = useCallback(async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  }, []);

  const customerLogin = useCallback(async (email, password) => {
    try {
      const { data } = await api.post("/customer/login", { email, password });
      setUser(data);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  }, []);

  const customerRegister = useCallback(async (payload) => {
    try {
      const { data } = await api.post("/customer/register", payload);
      setUser(data);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  }, []);

  const updateProfile = useCallback(async (patch) => {
    try {
      const { data } = await api.patch("/customer/me", patch);
      setUser(data);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (err) {
      // Logout endpoint is now public, but keep guard for cookie/network errors
      console.error("[auth] logout call failed:", err);
    }
    setUser(false);
  }, []);

  const value = useMemo(
    () => ({
      user,
      isAdmin: !!user && user.role === "admin",
      isCustomer: !!user && user.role === "customer",
      adminLogin,
      customerLogin,
      customerRegister,
      updateProfile,
      logout,
    }),
    [user, adminLogin, customerLogin, customerRegister, updateProfile, logout]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  return useContext(AuthContext);
}
